

/**
 * @typedef {"success" | "warning" | "info" | "error" | "neutral"} StatusTone
 */

/**
 * Maps each semantic tone to its legacy-palette classes (bg + text).
 *
 * There is deliberately NO `border` key any more. The old map paired a `-50`
 * fill with a `-200` border because a `-50` fill sitting on the v2 page
 * background (`--ops-carbon-25`, #FCFCFD) came out at ~1.03–1.08:1 — the pill
 * had no readable silhouette without an added edge. That argument is gone on
 * two counts: the chip now sits on a white legacy table card, and the fills
 * moved up to the `-100`/`-200` step, which reads as a badge on its own. It is
 * also what legacy actually does — every chip on `/all-orders` (`bg-elm-500
 * text-white-50` active, `bg-gray-200 text-[#333]` inactive) is borderless, and
 * a bordered chip next to those would be the very inconsistency this restyle
 * exists to remove.
 *
 * The old rejection of `-200` fills ("pushes text contrast under AA") was about
 * the ops token ramps, not these: `neutral` is byte-identical to a legacy
 * inactive filter chip, `success` is the `elm` ramp, the rest are stock
 * Tailwind, and all five clear 4.5:1 against their own fill.
 *
 * Read by `src/v2/ops/pages/tat-dashboard/components/TatTable.jsx` only. If you
 * reintroduce a third key here, that template needs it interpolated too.
 */
export const STATUS_TONE_CLASSES = {
    success: {
        bg: "bg-elm-100", // #d2f5eb
        text: "text-elm-700", // #1c695c
    },
    warning: {
        bg: "bg-yellow-100", // #fef9c3
        text: "text-yellow-800", // #854d0e
    },
    info: {
        bg: "bg-blue-100", // #dbeafe
        text: "text-blue-700", // #1d4ed8
    },
    error: {
        bg: "bg-red-100", // #fee2e2
        text: "text-[#dc143c]", // legacy's own crimson
    },
    neutral: {
        bg: "bg-gray-200", // #e5e7eb — legacy's inactive chip fill
        text: "text-[#333]", // legacy's own chip text
    },
};

/**
 * `code -> { label, tone }` for every order-status and delivery-status code
 * currently known to the app. One flat object: order-status codes (from the
 * Postgres enum, via `enum_userOrders_status`) and delivery-status codes
 * (from `DELIVERY_STATUS` in `src/components/delivery/constants.js`) don't
 * collide except for `DELIVERED`, which both domains use for the same
 * "delivered" concept with the same label/tone — so a single shared entry
 * is correct rather than a conflict. See task-6-report.md for details.
 */
export const STATUS_REGISTRY = {
    // --- Order status: "Order Pending" group -------------------------------
    ORDER_PENDING: { label: "Order Pending", tone: "warning" },
    ORDER_INACTIVE: { label: "Order Pending", tone: "warning" },
    ORDER_UPLOADRX: { label: "Order Pending", tone: "warning" },
    CALL_PENDING: { label: "Order Pending", tone: "warning" },
    CALL_PICKED: { label: "Order Pending", tone: "warning" },
    RX_SENT: { label: "Order Pending", tone: "warning" },
    RX_SEND: { label: "Order Pending", tone: "warning" },

    // --- Order status: everything else --------------------------------------
    ORDER_ACTIVE: { label: "Order Active", tone: "info" },
    // Same label as ORDER_ACTIVE, which already means this — near-certainly a
    // duplicate-meaning pair, same situation as RX_SENT/RX_SEND above.
    ORDER_CONFIRMED: { label: "Order Confirmed", tone: "info" },
    ORDER_CANCELLED: { label: "Order Cancelled", tone: "error" },

    // "Payment Pending" group
    CONSENT_GRANTED: { label: "Payment Pending", tone: "warning" },
    PAYMENT_PENDING: { label: "Payment Pending", tone: "warning" },

    PAYMENT_SUCCESS: { label: "Payment Success", tone: "success" },
    // Not part of the legacy orders.jsx filter groups this registry was
    // built from, but a live 2026 order status (see
    // pillup-api/docs/tat-dashboard/reference-status-vocabularies.md) — left
    // out, it would silently fall through to the raw-code neutral fallback.
    PAYMENT_CONFIRMED: { label: "Payment Confirmed", tone: "success" },
    // Payment confirmed with every medicine already in stock -- nothing to procure.
    // pillup-api moves an order here straight after PAYMENT_CONFIRMED
    // (.scratch/order-procured/PLAN.md there).
    ORDER_PROCURED: { label: "Order Procured", tone: "success" },

    // "OCS Pending" group
    OCS_PENDING: { label: "OCS Pending", tone: "warning" },
    OCS_PROCESSING: { label: "OCS Pending", tone: "warning" },

    USER_DRX_PENDING: { label: "DRX To Be Confirmed", tone: "warning" },
    // confirmRX_PENDING is an older/parallel naming for the same step as USER_DRX_PENDING.
    confirmRX_PENDING: { label: "DRX To Be Confirmed", tone: "warning" },
    // New LSQ webhook status (pillup-api migration 20260828160000): a digital-Rx record
    // exists but hasn't been sent to the patient yet, one step before ORDER_DRX_SENT.
    // This is a display label only — whether it should also join the Orders page's
    // "DRX Created" sub-tab (src/v2/ops/constants/orderSubTabs.js, currently
    // USER_DRX_PENDING/ACCEPTED/REJECTED) is a separate, follow-on decision.
    ORDER_DRX_CREATED: { label: "DRX Created", tone: "warning" },
    // Same real-world moment as USER_DRX_PENDING (sent, awaiting patient response), just
    // reached via the LSQ webhook path instead of the app.
    ORDER_DRX_SENT: { label: "DRX To Be Confirmed", tone: "warning" },

    // "Consent Pending" group
    USER_DRX_ACCEPTED: { label: "Consent Pending", tone: "warning" },
    // confirmRX_CONFIRM is the older/parallel counterpart to USER_DRX_ACCEPTED.
    confirmRX_CONFIRM: { label: "Consent Pending", tone: "warning" },
    CONSENT_PENDING: { label: "Consent Pending", tone: "warning" },

    // "Ready to Ship" group
    SHIPPED_MEDICINES_PACKED: { label: "Ready to Ship", tone: "info" },
    SHIPPED_BOX_PACKED: { label: "Ready to Ship", tone: "info" },
    MDM_QC: { label: "Ready to Ship", tone: "info" },
    SHIPPED_READY_TO_SHIP: { label: "Ready to Ship", tone: "info" },

    // Matches the delivery-domain PACKAGING entry below — same real-world step.
    SHIPPED_PACKAGING: { label: "Packaging", tone: "warning" },

    SHIPPED_IN_TRANSIT: { label: "In Transit", tone: "info" },
    // Carrier-specific in-transit variants — same concept, same label/tone.
    SHIPPED_VIA_PILLUP: { label: "In Transit", tone: "info" },
    SHIPPED_VIA_ZYDUS: { label: "In Transit", tone: "info" },

    // Shared with delivery status below (same concept, same label/tone).
    DELIVERED: { label: "Delivered", tone: "success" },

    // "Needs Attention" group
    SHIPPED_ERROR: { label: "Needs Attention", tone: "error" },
    USER_DRX_REJECTED: { label: "Needs Attention", tone: "error" },

    // --- Delivery status (src/components/delivery/constants.js) ------------
    PACKAGING: { label: "Packaging", tone: "warning" },
    PACKED: { label: "Packed", tone: "info" },
    READY_TO_SHIP: { label: "Ready to Ship", tone: "info" },
    IN_TRANSIT: { label: "In Transit", tone: "info" },
    OUT_FOR_DELIVERY: { label: "Out for Delivery", tone: "info" },
    // DELIVERED already registered above.
    CANCELLED: { label: "Cancelled", tone: "error" },
    RETURN: { label: "Returned", tone: "error" },
    ERROR: { label: "Error", tone: "error" },
};

const FALLBACK_ENTRY = { label: null, tone: "neutral" };

/**
 * Look up a status code's `{ label, tone }`. Unknown/missing codes fall back
 * to a neutral tone with the raw code (or an em dash) as the label, so
 * callers never have to null-check before rendering a badge.
 *
 * @param {string | null | undefined} code
 * @returns {{ label: string, tone: StatusTone }}
 */
export function getStatusMeta(code) {
    if (!code) return { label: "—", tone: FALLBACK_ENTRY.tone };
    const entry = STATUS_REGISTRY[code.toUpperCase?.() ?? code];
    if (entry) return entry;
    return { label: code, tone: FALLBACK_ENTRY.tone };
}
